import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import DualEngagementCurve from '../components/DualEngagementCurve';
import EngagementCurve from '../components/EngagementCurve';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.eonik.ai';

const T = {
  fg:      '#09090B',
  muted:   '#71717A',
  dim:     '#A1A1AA',
  border:  '#E4E4E7',
  bgSubtle:'#FAFAFA',
  bg:      '#FFFFFF',
  amber:   '#D97706',
};

const WINNER_CONFIG = {
  A:    { bg: '#F0FDF4', border: '#BBF7D0', accent: '#059669' },
  B:    { bg: '#FFFBEB', border: '#FDE68A', accent: '#D97706' },
  TIED: { bg: '#F9FAFB', border: '#E4E4E7', accent: '#6B7280' },
};

const CONFIDENCE_CONFIG = {
  high:   { label: 'HIGH CONFIDENCE', color: '#047857', bg: '#ECFDF3', border: '#A7F3D0' },
  medium: { label: 'MEDIUM CONFIDENCE', color: '#B45309', bg: '#FFFBEB', border: '#FDE68A' },
  low:    { label: 'LOW CONFIDENCE', color: '#7C2D12', bg: '#FFF7ED', border: '#FDBA74' },
};

export default function ShareReport() {
  const { token }  = useParams();
  const navigate   = useNavigate();
  const [report, setReport] = useState(null);
  const [error,  setError]  = useState('');
  const [time,   setTime]   = useState(0);

  useEffect(() => {
    if (!token) return;
    axios.get(`${BACKEND_URL}/api/tribe/share/${token}`)
      .then(r => setReport(r.data))
      .catch(e => {
        if (e.response?.status === 410) setError('This share link has expired.');
        else if (e.response?.status === 403) setError('Invalid or tampered share link.');
        else setError('Unable to load report. Please try again later.');
      });
  }, [token]);

  // All hooks must be called before any early returns (Rules of Hooks).
  const durationA      = report?.side_a?.duration_secs || 0;
  const durationB      = report?.side_b?.duration_secs || 0;
  const durationForDual = Math.max(durationA, durationB);

  const normalizedTimeline = useMemo(() => {
    const raw = report?.timeline || [];
    if (!raw.length || !durationForDual) return raw;
    const maxT = Math.max(...raw.map(p => Number(p?.t || 0)));
    if (maxT <= durationForDual * 1.15) return raw;
    const n = Math.max(1, raw.length - 1);
    return raw.map((p, idx) => ({ ...p, t: (idx / n) * durationForDual }));
  }, [report?.timeline, durationForDual]);

  if (error) return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: T.bg, padding: 24,
    }}>
      <div style={{ textAlign: 'center', maxWidth: 400 }}>
        <p style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: T.dim, letterSpacing: '0.1em', marginBottom: 12 }}>
          A/B · SHARE
        </p>
        <p style={{ fontSize: 15, color: T.muted, marginBottom: 24 }}>{error}</p>
        <button
          onClick={() => navigate('/')}
          style={{
            background: '#18181B', color: '#fff', border: 'none',
            borderRadius: 8, padding: '10px 22px', fontSize: 13,
            fontWeight: 600, cursor: 'pointer',
          }}
        >
          Try A/B
        </button>
      </div>
    </div>
  );

  if (!report) return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: T.bg,
    }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{
          width: 32, height: 32, border: '2px solid #E4E4E7',
          borderTopColor: '#18181B', borderRadius: '50%',
          animation: 'spin 0.8s linear infinite', margin: '0 auto 16px',
        }} />
        <p style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: T.dim, letterSpacing: '0.1em' }}>
          LOADING REPORT…
        </p>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  const winner = report.winner || 'TIED';
  const cfg    = WINNER_CONFIG[winner] || WINNER_CONFIG['TIED'];
  const confidenceKey = report.decision_confidence || 'low';
  const confidenceCfg = CONFIDENCE_CONFIG[confidenceKey] || CONFIDENCE_CONFIG.low;
  const labelA = report.label_a || 'Creative A';
  const labelB = report.label_b || 'Creative B';
  const winnerLabel = winner === 'A' ? labelA : winner === 'B' ? labelB : null;
  const recommendation = report.recommendation_strength || null;
  const recommendationText =
    recommendation === 'ship_now'
      ? 'Action: ship this variant now.'
      : recommendation === 'ship_with_monitoring'
        ? 'Action: ship with monitoring on first spend window.'
        : recommendation === 'validate_with_one_more_test'
          ? 'Action: run one more variant to validate before scaling.'
          : null;

  return (
    <div style={{ background: T.bg, minHeight: '100vh', fontFamily: 'system-ui, sans-serif' }}>
      {/* Top bar */}
      <nav style={{
        padding: '14px 24px', borderBottom: `1px solid ${T.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(12px)',
        position: 'sticky', top: 0, zIndex: 50,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img src="/assets/logo.svg" alt="eonik" style={{ height: 18, display: 'block' }} />
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: T.dim, letterSpacing: '0.06em' }}>
            · SHARED REPORT
          </span>
        </div>
        <button
          onClick={() => navigate('/')}
          style={{
            background: '#18181B', color: '#fff', border: 'none',
            borderRadius: 8, padding: '8px 18px', fontSize: 12,
            fontWeight: 600, cursor: 'pointer', letterSpacing: '0.02em',
          }}
        >
          Try A/B
        </button>
      </nav>

      <div style={{ maxWidth: 960, margin: '0 auto', padding: '32px 24px 64px' }}>
        {/* Watermark badge */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            border: `1px solid ${T.border}`, borderRadius: 100,
            padding: '4px 12px',
            fontFamily: 'var(--font-mono)', fontSize: 10, color: T.dim, letterSpacing: '0.08em',
          }}>
            fMRI-grade neural engagement · Powered by Tribe V2
          </span>
        </div>

        {/* Verdict hero */}
        <motion.div
          initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          style={{
            background: cfg.bg, border: `1px solid ${cfg.border}`,
            borderRadius: 16, padding: '36px 40px',
            textAlign: 'center', marginBottom: 28,
          }}
        >
          <p style={{
            fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.18em',
            color: T.muted, marginBottom: 10,
          }}>
            VERDICT
          </p>
          <h1 style={{
            fontSize: winner === 'TIED' ? 36 : 54,
            fontWeight: 800, letterSpacing: '-0.04em',
            color: cfg.accent, margin: '0 0 12px', lineHeight: 1,
          }}>
            {winner === 'TIED' ? 'TOO CLOSE TO CALL' : (winnerLabel || winner)}
          </h1>
          {winner !== 'TIED' && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center',
                padding: '5px 10px', borderRadius: 999,
                border: `1px solid ${confidenceCfg.border}`,
                background: confidenceCfg.bg, color: confidenceCfg.color,
                fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.08em',
              }}>
                {confidenceCfg.label}
              </span>
              {typeof report.decision_margin_pct === 'number' && (
                <span style={{
                  fontFamily: 'var(--font-mono)', fontSize: 11, color: T.muted,
                }}>
                  EDGE: {report.decision_margin_pct}%
                </span>
              )}
            </div>
          )}
          <p style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: T.dim, marginBottom: 16 }}>
            {labelA} vs {labelB}
          </p>
          {report.winner_reason && (
            <p style={{
              fontSize: 13, color: T.muted,
              fontFamily: 'var(--font-mono)', margin: 0,
              maxWidth: 500, marginLeft: 'auto', marginRight: 'auto',
              lineHeight: 1.65,
            }}>
              {report.winner_reason}
            </p>
          )}
          {winner !== 'TIED' && recommendationText && (
            <p style={{
              margin: '10px 0 0',
              fontSize: 12, color: T.dim, fontFamily: 'var(--font-mono)',
            }}>
              {recommendationText}
            </p>
          )}
        </motion.div>

        {/* Dual engagement curve */}
        {normalizedTimeline.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.12 }}
            style={{
              background: T.bgSubtle, border: `1px solid ${T.border}`,
              borderRadius: 16, padding: '24px 28px', marginBottom: 24,
            }}
          >
            <p style={{
              fontSize: 9, fontFamily: 'var(--font-mono)', color: T.dim,
              letterSpacing: '0.12em', marginBottom: 20,
            }}>
              NEURAL ENGAGEMENT COMPARISON · CLICK GRAPH TO SCRUB
            </p>
            <DualEngagementCurve
              timeline={normalizedTimeline}
              sideA={{ ...report.side_a, name: labelA }}
              sideB={{ ...report.side_b, name: labelB }}
              currentTime={time}
              onTimeChange={setTime}
            />
          </motion.div>
        )}

        {/* Side-by-side video players */}
        {(report.side_a?.video_url || report.side_b?.video_url) && (
          <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.15 }}
            style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}
          >
            {[
              { side: report.side_a, label: labelA, isWinner: winner === 'A' },
              { side: report.side_b, label: labelB, isWinner: winner === 'B' },
            ].map(({ side, label, isWinner }) => side?.video_url ? (
              <div key={label} style={{
                flex: '1 1 300px',
                background: '#000', border: `1px solid ${isWinner ? cfg.border : T.border}`,
                borderRadius: 12, overflow: 'hidden',
              }}>
                <div style={{
                  padding: '8px 14px',
                  background: T.bgSubtle, borderBottom: `1px solid ${T.border}`,
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700, color: T.fg }}>{label}</span>
                  {isWinner && (
                    <span style={{
                      background: cfg.bg, border: `1px solid ${cfg.border}`,
                      borderRadius: 100, padding: '1px 8px',
                      fontFamily: 'var(--font-mono)', fontSize: 9, color: cfg.accent, letterSpacing: '0.1em',
                    }}>WINNER</span>
                  )}
                </div>
                <video
                  src={side.video_url}
                  controls
                  playsInline
                  preload="metadata"
                  style={{ width: '100%', display: 'block', maxHeight: 240, objectFit: 'contain', background: '#000' }}
                />
              </div>
            ) : null)}
          </motion.div>
        )}

        {/* Per-side neural activation curves */}
        {(report.side_a?.attention?.length > 0 || report.side_b?.attention?.length > 0) && (
          <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.18 }}
            style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}
          >
            {[
              { side: report.side_a, label: labelA, duration: durationA, isWinner: winner === 'A' },
              { side: report.side_b, label: labelB, duration: durationB, isWinner: winner === 'B' },
            ].map(({ side, label, duration, isWinner }) => side?.attention?.length > 0 ? (
              <div key={label} style={{
                flex: '1 1 380px',
                background: T.bgSubtle, border: `1px solid ${isWinner ? cfg.border : T.border}`,
                borderRadius: 12, padding: '20px 24px',
              }}>
                <p style={{
                  fontSize: 9, fontFamily: 'var(--font-mono)', color: T.dim,
                  letterSpacing: '0.1em', marginBottom: 12,
                }}>
                  NEURAL ACTIVATION — {label}{isWinner ? ' · WINNER' : ''}
                </p>
                <EngagementCurve
                  insights={side}
                  currentTime={time}
                  onTimeChange={setTime}
                  videoDuration={duration || null}
                />
              </div>
            ) : null)}
          </motion.div>
        )}

        {/* Side-by-side stat panels */}
        {(report.side_a || report.side_b) && (
          <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}
          >
            {[
              { side: report.side_a, label: labelA, duration: durationA, isWinner: winner === 'A' },
              { side: report.side_b, label: labelB, duration: durationB, isWinner: winner === 'B' },
            ].map(({ side, label, duration, isWinner }) => side ? (
              <div key={label} style={{
                flex: '1 1 300px',
                background: T.bgSubtle, border: `1px solid ${isWinner ? cfg.border : T.border}`,
                borderRadius: 12, padding: '20px 24px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                  <span style={{
                    fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700,
                    color: T.fg, letterSpacing: '0.05em',
                  }}>{label}</span>
                  {isWinner && (
                    <span style={{
                      background: cfg.bg, border: `1px solid ${cfg.border}`,
                      borderRadius: 100, padding: '1px 8px',
                      fontFamily: 'var(--font-mono)', fontSize: 9, color: cfg.accent,
                      letterSpacing: '0.1em',
                    }}>WINNER</span>
                  )}
                </div>
                {[
                  ['AVG ACTIVATION', side.avg_activation?.toFixed(1)],
                  ['PEAK AT', (() => {
                    if (side.peak_second == null) return null;
                    const scale = duration && side.n_timesteps > duration * 1.1
                      ? duration / Math.max(1, side.n_timesteps - 1) : 1;
                    const realSec = Math.round(side.peak_second * scale);
                    const m = Math.floor(realSec / 60);
                    const s = realSec % 60;
                    return m > 0 ? `${m}:${s.toString().padStart(2, '0')}` : `0:${s.toString().padStart(2, '0')}`;
                  })()],
                  ['ATTENTION DROPS', side.drops?.length ?? 0],
                ].filter(([, v]) => v != null).map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: T.dim, letterSpacing: '0.07em' }}>{k}</span>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, color: T.fg }}>{v}</span>
                  </div>
                ))}
              </div>
            ) : null)}
          </motion.div>
        )}

        {/* Slack community strip — The Ad Gene */}
        <motion.div
          initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.25 }}
          style={{
            marginTop: 32, padding: '20px 24px',
            background: '#F8F7FF', border: '1px solid #C4B5FD',
            borderRadius: 12, display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', gap: 16, flexWrap: 'wrap',
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6 }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                background: '#4A154B', color: '#fff',
                borderRadius: 4, padding: '2px 8px',
                fontSize: 9, fontFamily: 'var(--font-mono)', letterSpacing: '0.1em', fontWeight: 700,
              }}>
                {/* Slack hash icon */}
                <svg width={9} height={9} viewBox="0 0 24 24" fill="currentColor" style={{ flexShrink: 0 }}>
                  <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z"/>
                </svg>
                #the-ad-gene
              </span>
              <span style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: '#7C3AED', letterSpacing: '0.08em' }}>SLACK CHANNEL</span>
            </div>
            <p style={{ margin: '0 0 3px', fontSize: 14, fontWeight: 700, color: '#1E1024' }}>
              You ran the science. Now see what the best do with it.
            </p>
            <p style={{ margin: 0, fontSize: 12, color: '#71717A' }}>
              Winning-ad teardowns, benchmark intel & creative learnings — the stuff that never makes it to newsletters.
            </p>
          </div>
          <a
            href="https://join.slack.com/t/theadgene/shared_invite/zt-3wd4nva82-ow~P0FJd7TqbLD9aIAvZlQ"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '10px 22px', borderRadius: 8,
              background: '#4A154B', color: '#fff',
              fontSize: 13, fontWeight: 600, textDecoration: 'none',
              whiteSpace: 'nowrap', flexShrink: 0,
              boxShadow: '0 2px 8px rgba(74,21,75,0.25)',
            }}
          >
            <svg width={14} height={14} viewBox="0 0 24 24" fill="currentColor" style={{ flexShrink: 0 }}>
              <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z"/>
            </svg>
            Join Community
          </a>
        </motion.div>

        {/* CTA footer */}
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          style={{
            marginTop: 24, padding: '32px 40px',
            background: '#18181B', borderRadius: 16,
            textAlign: 'center',
          }}
        >
          <p style={{
            fontFamily: 'var(--font-mono)', fontSize: 10, color: 'rgba(255,255,255,0.35)',
            letterSpacing: '0.12em', marginBottom: 12,
          }}>
            A/B · fMRI-GRADE CREATIVE INTELLIGENCE · EONIK.AI
          </p>
          <h2 style={{
            fontSize: 26, fontWeight: 800, color: '#fff',
            letterSpacing: '-0.03em', margin: '0 0 10px',
          }}>
            Know which of your ads wins.
          </h2>
          <p style={{
            fontSize: 13, color: 'rgba(255,255,255,0.5)',
            margin: '0 0 24px', maxWidth: 360, marginLeft: 'auto', marginRight: 'auto',
          }}>
            Get second-by-second attention data on your own ads — before you commit to media spend.
          </p>
          <button
            onClick={() => navigate('/')}
            style={{
              background: '#fff', color: '#18181B',
              border: 'none', borderRadius: 10,
              padding: '12px 28px', fontSize: 14, fontWeight: 700,
              cursor: 'pointer', letterSpacing: '-0.01em',
            }}
          >
            Try A/B — From $19
          </button>
        </motion.div>
      </div>
    </div>
  );
}
